/**
 * Document checklists — Documents_On_Site Prompt 9.
 *
 * Hard-coded per-report-type taxonomy of the documents that
 * meaningfully improve each report. Replaces the minimum-viable
 * checklist that shipped in Prompt 8 (`src/lib/documentChecklist.ts`,
 * which now re-exports from here for back-compat).
 *
 * The shape is richer:
 *   - severity drives the colour + the confidence floor cap.
 *   - relevanceTagPattern is matched against a doc's fileName + tags +
 *     summary so an uploaded "Phase I ESA — 2024.pdf" satisfies the
 *     phase1-esa slot without the user having to map by hand.
 *   - sedSectionsHelpful keeps this in sync with the SED section
 *     catalogue (siteEvidence.SedSectionId) so a future Prompt-2
 *     classifier rerun can suggest matches automatically.
 *   - replaceableBy lets one slot be satisfied by an alternate; e.g.
 *     a Phase II ESA can stand in for a Phase I in some workflows.
 */
import type { ReportTypeId } from './reportTypes';
import { documentContentNamesRequirement } from '../services/knowledge/documentTopics';
import type { SiteDocument } from '../types/siteRag';
import type { SedSectionId, SiteEvidenceDocument } from '../types/siteEvidence';
import type { AuthoredFieldDef } from '../types/authoredEvidence';
import { isLibraryIndexDoc } from '../lib/knowledge/libraryIndexDocument';
import { throwIfAborted } from '../lib/abortSignal';
import { getReportSetupChecklistFor } from './reportSetupRequirements';

export type ChecklistSeverity = 'recommended' | 'optional' | 'critical';

export type ChecklistItemKind = 'document' | 'report-setup' | 'site-intent';

/**
 * P3.8 follow-through — verdict of an evidence-probed row. `reason` explains a
 * `missing` verdict in the user's terms (never searched vs. searched and barren).
 */
export type EvidenceProbeId = 'public-sentiment-scan';

export interface EvidenceProbeVerdict {
  status: 'strong' | 'missing';
  reason?: string;
}

export interface ChecklistItem {
  /** Stable id used for replaceableBy + match audit logs. */
  id: string;
  /** Display label rendered in the panel. */
  label: string;
  /** One-line "why it matters" — surfaces in tooltips and the report-time banner. */
  description: string;
  /** SED sections this doc strengthens. Mirrors SiteDocument.sedSectionsHelpful. */
  sedSectionsHelpful: SedSectionId[];
  /** Lower-case substring patterns matched against fileName + tags + summary. */
  relevanceTagPattern: string[];
  severity: ChecklistSeverity;
  /** Alternate item ids that satisfy this slot (e.g. ['phase2-esa'] for Phase I). */
  replaceableBy?: string[];
  /**
   * P3.8 follow-through — an EVIDENCE-PROBED row: the SED's own scan decides
   * the status. No authored fields, no document tier; `computeRequirementStatus`
   * consults this first and the coverage panel offers the scan as the action.
   */
  evidenceProbe?: (sed: SiteEvidenceDocument | null | undefined) => EvidenceProbeVerdict;
  /**
   * Data-only handle for an evidence probe resolved OUTSIDE config
   * (`lib/coverage/evidenceProbes.ts`). Config must not import the probe:
   * the sentiment probe reaches the sentiment orchestrator and its audit-log
   * client, and every consumer of the checklists (the store included) would
   * inherit that import chain.
   */
  evidenceProbeId?: EvidenceProbeId;
  /**
   * Estimated score lift in percentage points when this slot is
   * filled. Drives the "+Npt" suffix on the post-report
   * "We can improve this report" suggestions (Preflight Prompt 5).
   * When omitted, the suggestion engine falls back to a
   * severity-derived default (critical → 20, recommended → 12,
   * optional → 5). Set explicit values on items where field-tuned
   * impact is known so the user sees a precise number rather than
   * the bucket default.
   */
  lift?: number;
  /**
   * UAE-1 — schema for the per-requirement `[Author]` dialog. When
   * present, clicking the Coverage Matrix `[Author]` button renders
   * these fields in the dialog and persists the values as a
   * `'user-typed'` `ExtractedClaim` with `requirementId = this.id`,
   * `authoredKind: 'structured'`, and `structuredFields` carrying the
   * form values. When absent, the dialog falls back to a prose-only
   * `notes` textarea (UAE-6 still exposes the Author action — prose
   * is a first-class authoring path).
   */
  authoredSchema?: readonly AuthoredFieldDef[];
  /**
   * Row kind — document attestation (default), wizard typed-prefs setup,
   * or read-only Site Project Intent. Non-document rows suppress upload
   * actions and resolve values from intent / typedPreferencesDefaults.
   */
  kind?: ChecklistItemKind;
}

/**
 * UAE-6 — universal `notes` field appended to every requirement's
 * `authoredSchema`. Lets a user fill in any requirement with prose
 * even when the structured fields don't apply / aren't known. Defined
 * once here so every requirement's notes field has identical id +
 * label + placeholder.
 */
const NOTES_FIELD: AuthoredFieldDef = {
  key: 'notes',
  label: 'Notes',
  type: 'textarea',
  required: false,
  placeholder: 'e.g. verified verbally with consultant X on 2026-05-17; document expected Q3 2026.',
  help: 'Free-form context — used when structured fields don\'t apply or are unknown.',
};

const ALTA_SURVEY_SCHEMA: readonly AuthoredFieldDef[] = [
  { key: 'standardYear', label: 'ALTA/NSPS standard year', type: 'enum', enumValues: ['2021', '2026', 'other'], required: false },
  { key: 'surveyor', label: 'Surveyor / firm', type: 'string', required: false },
  { key: 'surveyDate', label: 'Survey date', type: 'date', required: false },
  { key: 'surveyedAcreage', label: 'Surveyed acreage', type: 'number', unit: 'acres', required: false },
  { key: 'legalDescriptionConfirmed', label: 'Legal description confirmed?', type: 'boolean', required: false },
  { key: 'accessConfirmed', label: 'Access confirmed?', type: 'boolean', required: false },
  { key: 'easements', label: 'Easements', type: 'list', required: false },
  { key: 'rightsOfWay', label: 'Rights-of-way', type: 'list', required: false },
  { key: 'encroachments', label: 'Encroachments', type: 'list', required: false },
  { key: 'utilitiesObserved', label: 'Utilities observed', type: 'list', required: false },
  { key: 'tableAItems', label: 'Table A items', type: 'list', required: false },
  { key: 'floodZone', label: 'Flood notation', type: 'string', required: false },
  { key: 'contoursIncluded', label: 'Documented contours included?', type: 'boolean', required: false },
  { key: 'contourInterval_ft', label: 'Contour interval', type: 'number', unit: 'ft', required: false },
  { key: 'verticalDatum', label: 'Vertical datum', type: 'string', required: false },
  NOTES_FIELD,
];

const ALTA_SURVEY_ITEM: ChecklistItem = {
  id: 'alta-survey',
  label: 'ALTA/NSPS land title survey',
  severity: 'recommended',
  sedSectionsHelpful: ['survey', 'parcel', 'topography'],
  relevanceTagPattern: ['alta', 'nsps', 'land title survey', 'table a'],
  description: 'Documents title-survey matters. It supplies contours only when the commissioned scope and datum explicitly include them, and does not replace the critical boundary/topographic survey for design.',
  lift: 14,
  authoredSchema: ALTA_SURVEY_SCHEMA,
};

const MINERAL_TITLE_SCHEMA: readonly AuthoredFieldDef[] = [
  { key: 'preparer', label: 'Preparer / title professional', type: 'string', required: false },
  { key: 'reportDate', label: 'Report date', type: 'date', required: false },
  { key: 'countyRecordReference', label: 'County record reference', type: 'string', required: false },
  { key: 'ownershipStatus', label: 'Mineral ownership status', type: 'enum', enumValues: ['unified', 'severed', 'reserved', 'leased', 'unknown'], required: false },
  { key: 'reservedCommodities', label: 'Reserved commodities', type: 'list', required: false },
  { key: 'activeLeases', label: 'Active leases', type: 'list', required: false },
  { key: 'surfaceUseRestrictions', label: 'Surface-use restrictions', type: 'list', required: false },
  NOTES_FIELD,
];

const MINERAL_TITLE_ITEM: ChecklistItem = {
  id: 'mineral-title',
  label: 'Mineral title / ownership report',
  severity: 'recommended',
  sedSectionsHelpful: ['mineralResources'],
  relevanceTagPattern: ['mineral title', 'mineral rights', 'severance', 'reservation', 'mineral lease', 'county records'],
  description: 'Establishes ownership, severance, reservations, leases, and surface-use restrictions from client, county, or title-professional evidence. Public occurrence screens cannot substitute.',
  lift: 13,
  authoredSchema: MINERAL_TITLE_SCHEMA,
};

const ARCHAEOLOGICAL_SURVEY_SCHEMA: readonly AuthoredFieldDef[] = [
  { key: 'qualifiedProfessional', label: 'Qualified professional / firm', type: 'string', required: false },
  { key: 'surveyDate', label: 'Survey date', type: 'date', required: false },
  { key: 'surveyLevel', label: 'Survey scope / level', type: 'string', required: false },
  { key: 'acreageSurveyed', label: 'Acreage surveyed', type: 'number', unit: 'acres', required: false },
  { key: 'resourcesIdentified', label: 'Resources identified?', type: 'boolean', required: false },
  { key: 'resourceCount', label: 'Resource count', type: 'number', required: false },
  { key: 'eligibilityRecommendation', label: 'Eligibility recommendation', type: 'string', required: false },
  { key: 'effectFinding', label: 'Agency effect finding', type: 'string', required: false },
  { key: 'shpoCaseNumber', label: 'SHPO/THPO case status or number', type: 'string', required: false },
  NOTES_FIELD,
];

const ARCHAEOLOGICAL_SURVEY_ITEM: ChecklistItem = {
  id: 'archaeological-survey',
  label: 'Archaeological survey / agency finding',
  severity: 'recommended',
  sedSectionsHelpful: ['culturalResources'],
  relevanceTagPattern: ['archaeological survey', 'cultural resources survey', 'phase i cultural', 'shpo', 'thpo', 'section 106'],
  description: 'Provides professional survey scope and agency/SHPO/THPO status when required. Sensitive resource coordinates are never extracted, logged, prompted, or reported.',
  lift: 15,
  authoredSchema: ARCHAEOLOGICAL_SURVEY_SCHEMA,
};

/** Shared BTM vendor-quote schema — cross-listed on feasibility, power, supply-chain, and executive-summary. */
const BTM_VENDOR_QUOTE_SCHEMA: readonly AuthoredFieldDef[] = [
  {
    key: 'technology',
    label: 'Primary technology',
    type: 'enum',
    enumValues: ['gas', 'solar', 'wind', 'bess', 'hybrid', 'other'],
    required: false,
  },
  { key: 'capacity_MW', label: 'Quoted capacity', type: 'number', unit: 'MW', required: false },
  { key: 'lcoe_USDperMWh', label: 'Indicative LCOE', type: 'number', unit: 'USD/MWh', required: false },
  { key: 'leadTime_months', label: 'Lead time', type: 'number', unit: 'months', required: false },
  { key: 'vendorName', label: 'Vendor / OEM', type: 'string', required: false },
  { key: 'ambientTemp_C', label: 'Ambient temperature', type: 'number', unit: '°C', required: false },
  { key: 'wetBulb_C', label: 'Wet bulb', type: 'number', unit: '°C', required: false },
  { key: 'peakTemp_C', label: 'Peak temperature', type: 'number', unit: '°C', required: false },
  { key: 'humidity_pct', label: 'Relative humidity', type: 'number', unit: '%', required: false },
  { key: 'waterStressLevel', label: 'Water stress level', type: 'string', required: false },
  { key: 'elevation_m', label: 'Elevation', type: 'number', unit: 'm', required: false },
  { key: 'nearestTransmissionKm', label: 'Nearest transmission corridor', type: 'number', unit: 'km', required: false },
  { key: 'substationDistKm', label: 'Nearest substation distance', type: 'number', unit: 'km', required: false },
  { key: 'nearestPipelineKm', label: 'Nearest pipeline distance', type: 'number', unit: 'km', required: false },
  { key: 'nearestPipelineOperator', label: 'Nearest pipeline operator', type: 'string', required: false },
  { key: 'nearestPipelineAvailabilityPct', label: 'Nearest pipeline availability', type: 'number', unit: '%', required: false },
  { key: 'nearestPipelineMassFlowValue', label: 'Nearest pipeline mass flow', type: 'number', required: false },
  { key: 'nearestPipelineMassFlowUnit', label: 'Nearest pipeline mass flow unit', type: 'string', required: false },
  // A pipeline QUANTITY is only promoted to firm, site-deliverable gas capacity
  // when its basis says so explicitly (`explicitFirmSiteDeliveryBasis` in
  // screeningEvidencePrefill.ts). Without a basis field the checklist could
  // capture a value and a unit but never a basis, so every user-entered flow was
  // rejected as `not-firm-site-deliverable` — the gate was UNREACHABLE rather
  // than strict. An enum, not free text: the gate needs both a firm word and a
  // site-delivery word, and guessing that phrasing is not the user's job.
  // 'indicative-throughput' is the honest default — it records what the document
  // says without claiming contracted capacity.
  { key: 'nearestPipelineMassFlowBasis', label: 'Pipeline quantity basis', type: 'enum', enumValues: ['indicative-throughput', 'firm site-deliverable capacity', 'contracted delivery point capacity', 'reserved metered capacity', 'committed site interconnect capacity'], required: false },
  NOTES_FIELD,
];

const BTM_VENDOR_QUOTE_ITEM: ChecklistItem = {
  id: 'btm-vendor-quote',
  label: 'BTM generation vendor quote or study',
  severity: 'recommended',
  sedSectionsHelpful: ['solar', 'wind', 'electricity', 'grid'],
  relevanceTagPattern: ['btm', 'behind-the-meter', 'gas turbine', 'bess', 'solar', 'generator', 'cogeneration'],
  description: 'Anchors BTM technology choice, capacity, economics, and timeline with vendor evidence.',
  lift: 16,
  authoredSchema: BTM_VENDOR_QUOTE_SCHEMA,
};

/** Shared vendor-quote-pack schema — supply-chain report + executive synthesis. */
const VENDOR_QUOTE_PACK_SCHEMA: readonly AuthoredFieldDef[] = [
  { key: 'supplierCount', label: 'Suppliers quoted', type: 'number', required: false },
  { key: 'longestLeadMonths', label: 'Longest lead time', type: 'number', unit: 'months', required: false },
  { key: 'quoteCoverage_pct', label: 'BoM quote coverage', type: 'number', unit: '%', required: false },
  {
    key: 'topRiskCategories',
    label: 'Top risk categories',
    type: 'string',
    required: false,
    placeholder: 'e.g. generators, switchgear, cooling',
  },
  NOTES_FIELD,
];

const VENDOR_QUOTE_PACK_ITEM: ChecklistItem = {
  id: 'vendor-quote-pack',
  label: 'Vendor quote pack or budgetary proposals',
  severity: 'recommended',
  sedSectionsHelpful: ['siteResearch', 'grid'],
  relevanceTagPattern: ['quote', 'vendor', 'supplier', 'lead time', 'procurement', 'bom'],
  description: 'Anchors supplier, lead-time, and pricing risk with auditable external evidence.',
  authoredSchema: VENDOR_QUOTE_PACK_SCHEMA,
};

/** City/district service statements retain the same ResourceV2 fields and
 * attribution in the active screen and historical water-resource reports. */
const MUNICIPAL_UTILITY_LETTER_ITEM: ChecklistItem = {
  id: 'municipal-utility-letter',
  label: 'Municipal water and wastewater service letter or questionnaire',
  severity: 'recommended',
  sedSectionsHelpful: ['waterResource', 'waterStress'],
  relevanceTagPattern: ['water provider', 'wastewater', 'sewer', 'mgd', 'utility questionnaire', 'water treatment', 'lift station', 'water main'],
  description: 'City or district statement of potable supply and wastewater capacity, connection distance and rates. Without it, municipal service is assumed from regional data.',
  authoredSchema: [
    { key: 'supplyProvider', label: 'Water provider', type: 'string', required: false, placeholder: 'e.g. City of DeRidder' },
    { key: 'supplySourceDescription', label: 'Water source', type: 'string', required: false, placeholder: 'e.g. groundwater from the Chicot Aquifer' },
    { key: 'supplyAvgMgd', label: 'Average water production', type: 'number', unit: 'MGD', required: false },
    { key: 'supplyPeakMgd', label: 'Peak water production', type: 'number', unit: 'MGD', required: false },
    { key: 'supplyPermittedMgd', label: 'Permitted water capacity', type: 'number', unit: 'MGD', required: false },
    { key: 'supplyExcessMgd', label: 'Excess water capacity', type: 'number', unit: 'MGD', required: false },
    { key: 'siteWaterAllocationMgd', label: 'Water allocated to this site', type: 'number', unit: 'MGD', required: false, help: 'Site-specific allotment, not utility system production/capacity or project water demand. Retain assumed/fictional and service conditions.' },
    { key: 'projectAverageWaterDemandMgd', label: 'Project average water demand', type: 'number', unit: 'MGD', required: false, help: 'Project demand, not municipal system production or the site allocation.' },
    { key: 'projectPeakWaterDemandMgd', label: 'Project peak water demand', type: 'number', unit: 'MGD', required: false, help: 'Project demand, not municipal system peak production or the site allocation.' },
    { key: 'projectWaterMarginMgd', label: 'Project water allocation margin', type: 'number', unit: 'MGD', required: false, help: 'Source-stated allocation less project peak demand; not municipal system excess capacity.' },
    { key: 'siteWastewaterAllocationMgd', label: 'Wastewater allocated to this site', type: 'number', unit: 'MGD', required: false, help: 'Site-specific wastewater allocation, not municipal treatment-plant capacity.' },
    { key: 'projectPeakWastewaterDischargeMgd', label: 'Project peak wastewater discharge', type: 'number', unit: 'MGD', required: false, help: 'Project discharge, not the treatment-plant peak operating flow.' },
    { key: 'projectWastewaterMarginMgd', label: 'Project wastewater allocation margin', type: 'number', unit: 'MGD', required: false, help: 'Source-stated wastewater allocation less project discharge; not treatment-plant excess capacity.' },
    { key: 'minimumWaterPressurePsi', label: 'Source-stated minimum site supply pressure', type: 'number', unit: 'psi', required: false },
    { key: 'waterServiceDate', label: 'Source-stated site water service date', type: 'date', required: false },
    { key: 'supplyStorageGallons', label: 'Water storage', type: 'number', unit: 'gallons', required: false },
    { key: 'supplyNearestMainMiles', label: 'Nearest water main', type: 'number', unit: 'miles', required: false },
    { key: 'supplyLineToSite', label: 'Water line reaches the site?', type: 'boolean', required: false },
    { key: 'wastewaterProvider', label: 'Wastewater provider', type: 'string', required: false },
    { key: 'wastewaterPlantDescription', label: 'Treatment plant', type: 'string', required: false, placeholder: 'e.g. activated sludge' },
    { key: 'wastewaterPermittedMgd', label: 'Permitted treatment capacity', type: 'number', unit: 'MGD', required: false },
    { key: 'wastewaterAvgMgd', label: 'Average treatment flow', type: 'number', unit: 'MGD', required: false },
    { key: 'wastewaterPeakMgd', label: 'Peak treatment flow', type: 'number', unit: 'MGD', required: false },
    { key: 'wastewaterExcessMgd', label: 'Excess treatment capacity', type: 'number', unit: 'MGD', required: false },
    { key: 'wastewaterNearestConnectionMiles', label: 'Nearest sewer connection', type: 'number', unit: 'miles', required: false },
    { key: 'wastewaterLineToSite', label: 'Sewer line reaches the site?', type: 'boolean', required: false },
    { key: 'wastewaterConnectionType', label: 'Connection type', type: 'enum', enumValues: ['gravity', 'force-main'], required: false },
    { key: 'statedBy', label: 'Stated by', type: 'string', required: false, placeholder: 'e.g. Roy Keen, Director of Utilities, City of DeRidder' },
    { key: 'statedOn', label: 'Stated on', type: 'date', required: false },
    { key: 'appliesToProject', label: 'Prepared for', type: 'enum', enumValues: ['this-site', 'other-project', 'unknown'], required: false, help: 'A questionnaire answered for another project is indicative, not site-specific.' },
    NOTES_FIELD,
  ],
};

/** Evidence follows the water screen into feasibility; legacy reports keep it. */
const WATER_SCREEN_DOCUMENTS: ChecklistItem[] = [
    {
      id: 'water-rights',
      label: 'Water rights certificate or appropriation',
      severity: 'critical',
      sedSectionsHelpful: ['waterStress'],
      relevanceTagPattern: ['water right', 'appropriation', 'water permit'],
      description: 'Mandatory in prior-appropriation states. Without it, conclusions are state-doctrine inferences.',
      authoredSchema: [
        { key: 'permittedVolume_MGD', label: 'Permitted volume', type: 'number', unit: 'MGD', required: false },
        { key: 'waterSource', label: 'Water source', type: 'string', required: false, placeholder: 'e.g. surface, groundwater, municipal' },
        { key: 'priorityDate', label: 'Priority date', type: 'date', required: false },
        NOTES_FIELD,
      ],
    },
    {
      id: 'aquifer-study',
      label: 'Aquifer / groundwater study',
      severity: 'recommended',
      sedSectionsHelpful: ['waterStress', 'siteResearch'],
      relevanceTagPattern: ['aquifer', 'groundwater', 'well'],
      description: 'Site-specific aquifer depth; without it, drought sensitivity is regional only.',
      authoredSchema: [
        { key: 'aquiferName', label: 'Aquifer name', type: 'string', required: false },
        { key: 'staticWaterLevel_ft', label: 'Static water level', type: 'number', unit: 'ft bgs', required: false },
        { key: 'estimatedYield_gpm', label: 'Estimated yield', type: 'number', unit: 'gpm', required: false },
        NOTES_FIELD,
      ],
    },
  MUNICIPAL_UTILITY_LETTER_ITEM,
];

/** Fiber access supports early screening and the later connectivity study. */
export const FIBER_SURVEY_ITEM: ChecklistItem = {
      id: 'fiber-survey',
      label: 'Fiber survey or carrier quote',
      severity: 'critical',
      sedSectionsHelpful: ['connectivity'],
      relevanceTagPattern: ['fiber', 'carrier', 'quote'],
      description: 'PeeringDB distances are coarse; site-specific fiber path matters.',
      authoredSchema: [
        { key: 'carrier', label: 'Carrier', type: 'string', required: true, placeholder: 'e.g. Lumen, Zayo, Cox Business' },
        {
          key: 'serviceType',
          label: 'Service type',
          type: 'enum',
          enumValues: ['dark fiber', 'lit wave', 'dedicated internet', 'wavelength', 'other'],
          required: false,
        },
        { key: 'capacity_Gbps', label: 'Capacity offered', type: 'number', unit: 'Gbps', required: false },
        { key: 'buildDistance_ft', label: 'Build distance to handoff', type: 'number', unit: 'ft', required: false },
        { key: 'quoteValidUntil', label: 'Quote valid until', type: 'date', required: false },
        { key: 'mrc_USD', label: 'Monthly recurring cost', type: 'number', unit: 'USD', required: false },
        NOTES_FIELD,
      ],
    };

export const DOC_CHECKLISTS: Record<ReportTypeId, ChecklistItem[]> = {
  feasibility: [
    FIBER_SURVEY_ITEM,
    ...WATER_SCREEN_DOCUMENTS,
    {
      id: 'geotech',
      label: 'Geotechnical report or borehole logs',
      severity: 'recommended',
      sedSectionsHelpful: ['geotechnical', 'seismic', 'parcel'],
      relevanceTagPattern: ['geotech', 'soil', 'borehole', 'foundation'],
      description: 'Provides boreholes and design-grade foundation conclusions. USGS/SSURGO public screening does not substitute for this professional report.',
      // Slightly higher than the recommended default — geotech
      // ground-truths the seismic + parcel sections together.
      lift: 15,
      authoredSchema: [
        { key: 'bearingCapacity_kPa', label: 'Bearing capacity', type: 'number', unit: 'kPa', required: false },
        {
          key: 'soilClassification',
          label: 'Soil classification (USCS)',
          type: 'enum',
          enumValues: ['SP', 'SM', 'SC', 'ML', 'CL', 'CH', 'OL', 'OH', 'Pt'],
          required: false,
        },
        { key: 'boreholeDepth_m', label: 'Borehole depth', type: 'number', unit: 'm', required: false },
        { key: 'groundwaterDepth_m', label: 'Groundwater depth', type: 'number', unit: 'm', required: false },
        { key: 'preparedBy', label: 'Prepared by', type: 'string', required: false },
        { key: 'reportDate', label: 'Report date', type: 'date', required: false },
        { key: 'foundationRecommendation', label: 'Foundation recommendation', type: 'textarea', required: false },
        { key: 'settlementRisk', label: 'Settlement risk', type: 'string', required: false },
        { key: 'liquefactionRisk', label: 'Liquefaction risk', type: 'string', required: false },
        { key: 'expansiveSoilRisk', label: 'Expansive-soil risk', type: 'string', required: false },
        { key: 'seismicSiteClass', label: 'Seismic site class', type: 'string', required: false },
        NOTES_FIELD,
      ],
    },
    {
      id: 'utility-letter',
      label: 'Utility service letter / interconnection study',
      severity: 'recommended',
      sedSectionsHelpful: ['grid'],
      relevanceTagPattern: ['utility', 'interconnect', 'service letter', 'capacity'],
      description: 'Records utility-stated site capacity and timing. HIFLD distance provides topology context only; without applicable evidence, offered capacity and service timing remain unknown while early screening can continue.',
      // Field-tuned: a real interconnection study collapses the
      // largest band of grid-side uncertainty in feasibility
      // reports, so the lift is materially above the "recommended"
      // default of 12pt.
      lift: 18,
      authoredSchema: [
        { key: 'utilityName', label: 'Utility name', type: 'string', required: true, placeholder: 'e.g. Entergy Louisiana' },
        { key: 'serviceDate', label: 'Service date', type: 'date', required: false },
        { key: 'capacityOffered_MW', label: 'Capacity offered', type: 'number', unit: 'MW', required: false },
        {
          key: 'interconnectionClass',
          label: 'Interconnection class',
          type: 'enum',
          enumValues: ['firm', 'non-firm', 'cluster', 'energy-only', 'network'],
          required: false,
        },
        { key: 'costEstimate_USD', label: 'Cost estimate', type: 'number', unit: 'USD', required: false },
        { key: 'nearestTransmissionKm', label: 'Nearest transmission corridor', type: 'number', unit: 'km', required: false },
        { key: 'substationDistKm', label: 'Nearest substation distance', type: 'number', unit: 'km', required: false },
        { key: 'nearestPipelineKm', label: 'Nearest pipeline distance', type: 'number', unit: 'km', required: false },
        { key: 'nearestPipelineOperator', label: 'Nearest pipeline operator', type: 'string', required: false },
        { key: 'nearestPipelineAvailabilityPct', label: 'Nearest pipeline availability', type: 'number', unit: '%', required: false },
        { key: 'nearestPipelineMassFlowValue', label: 'Nearest pipeline mass flow', type: 'number', required: false },
        { key: 'nearestPipelineMassFlowUnit', label: 'Nearest pipeline mass flow unit', type: 'string', required: false },
        { key: 'nearestPipelineMassFlowBasis', label: 'Pipeline quantity basis', type: 'enum', enumValues: ['indicative-throughput', 'firm site-deliverable capacity', 'contracted delivery point capacity', 'reserved metered capacity', 'committed site interconnect capacity'], required: false },
        NOTES_FIELD,
      ],
    },
    {
      id: 'parcel-survey',
      label: 'Title / parcel survey',
      severity: 'recommended',
      sedSectionsHelpful: ['parcel', 'siteProfile'],
      relevanceTagPattern: ['title', 'survey', 'parcel', 'deed'],
      description: 'Verifies acreage, easements, encroachments. Without it, parcel inferred from Regrid only.',
      authoredSchema: [
        { key: 'parcelId', label: 'Parcel ID', type: 'string', required: true, placeholder: 'e.g. APN 12-345-6789' },
        { key: 'recordedAcreage', label: 'Recorded acreage', type: 'number', unit: 'acres', required: false },
        { key: 'netUsableAcres', label: 'Source-stated net usable / buildable area', type: 'number', unit: 'acres', required: false, help: 'Separate from gross parcel acreage; a source statement is not an engineered capacity or professional fit result.' },
        { key: 'deedRestrictions', label: 'Deed restrictions', type: 'textarea', required: false, placeholder: 'Any covenants, conditions, or restrictions.' },
        { key: 'easements', label: 'Easements', type: 'list', required: false, placeholder: 'Comma-separated, e.g. utility, drainage, access' },
        NOTES_FIELD,
      ],
    },
    ALTA_SURVEY_ITEM,
    MINERAL_TITLE_ITEM,
    {
      id: 'zoning',
      label: 'Zoning verification or letter of intent',
      severity: 'recommended',
      sedSectionsHelpful: ['parcel', 'regulatory'],
      relevanceTagPattern: ['zoning', 'land use'],
      description: 'Without it, zoning inferred from Regrid metadata which may be stale.',
      authoredSchema: [
        { key: 'zoningCode', label: 'Zoning code', type: 'string', required: true, placeholder: 'e.g. M-2, I-1, RR-5' },
        { key: 'permittedUses', label: 'Permitted uses', type: 'list', required: false, placeholder: 'Comma-separated, e.g. industrial, utility, manufacturing' },
        { key: 'setbacks_ft', label: 'Setbacks', type: 'number', unit: 'ft', required: false },
        { key: 'heightLimit_ft', label: 'Height limit', type: 'number', unit: 'ft', required: false },
        { key: 'specialConditions', label: 'Special conditions', type: 'textarea', required: false },
        NOTES_FIELD,
      ],
    },
    {
      id: 'flood-cert',
      label: 'Flood elevation certificate',
      severity: 'optional',
      sedSectionsHelpful: ['flood'],
      relevanceTagPattern: ['flood', 'elevation cert', 'fema'],
      description: 'Site-specific elevation overrides FEMA NFHL zone resolution.',
      authoredSchema: [
        { key: 'bfe_ft', label: 'Base Flood Elevation (BFE)', type: 'number', unit: 'ft', required: false },
        {
          key: 'floodZone',
          label: 'Flood zone',
          type: 'enum',
          enumValues: ['X', 'A', 'AE', 'AH', 'AO', 'VE', 'V', 'D'],
          required: false,
        },
        {
          key: 'datum',
          label: 'Datum',
          type: 'enum',
          enumValues: ['NAVD88', 'NGVD29'],
          required: false,
        },
        { key: 'topOfLowestFloor_ft', label: 'Top of lowest floor', type: 'number', unit: 'ft', required: false },
        NOTES_FIELD,
      ],
    },
    BTM_VENDOR_QUOTE_ITEM,
  ],
  environmental: [
    {
      id: 'phase1-esa',
      label: 'Phase I Environmental Site Assessment',
      severity: 'critical',
      sedSectionsHelpful: ['environmental', 'siteResearch'],
      relevanceTagPattern: ['phase 1', 'phase i', 'esa', 'environmental site'],
      description: 'Foundation document for any environmental analysis. Without it the report is preliminary at best.',
      replaceableBy: ['phase2-esa'],
      authoredSchema: [
        { key: 'preparer', label: 'Preparer', type: 'string', required: false, placeholder: 'Consultant or firm name' },
        { key: 'completedDate', label: 'Completed date', type: 'date', required: false },
        { key: 'recRecognized', label: 'Recognized Environmental Conditions (RECs)?', type: 'boolean', required: false },
        { key: 'recDetails', label: 'REC details', type: 'textarea', required: false },
        NOTES_FIELD,
      ],
    },
    {
      id: 'wetland',
      label: 'Wetland delineation or NWI report',
      severity: 'recommended',
      sedSectionsHelpful: ['environmental'],
      relevanceTagPattern: ['wetland', 'nwi', 'delineation'],
      description: 'Site-specific wetland boundaries override NWI raster.',
      authoredSchema: [
        { key: 'wetlandsPresent', label: 'Wetlands present on site?', type: 'boolean', required: false },
        { key: 'wetlandAcres', label: 'Wetland acreage', type: 'number', unit: 'acres', required: false },
        { key: 'wetlandType', label: 'Wetland type', type: 'string', required: false, placeholder: 'e.g. PEM (palustrine emergent), PFO (forested)' },
        NOTES_FIELD,
      ],
    },
    {
      id: 'species-survey',
      label: 'Threatened/endangered species survey',
      severity: 'recommended',
      sedSectionsHelpful: ['environmental'],
      relevanceTagPattern: ['species', 'esa', 'habitat', 'usfws'],
      description: 'Without it, species concerns are inferred from USFWS range maps only.',
      authoredSchema: [
        { key: 'speciesObserved', label: 'Listed species observed', type: 'list', required: false, placeholder: 'Comma-separated' },
        { key: 'criticalHabitat', label: 'Critical habitat designated?', type: 'boolean', required: false },
        { key: 'surveyDate', label: 'Survey date', type: 'date', required: false },
        NOTES_FIELD,
      ],
    },
    {
      id: 'cultural',
      label: 'Cultural resources / SHPO clearance',
      severity: 'optional',
      sedSectionsHelpful: ['culturalResources'],
      relevanceTagPattern: ['cultural', 'shpo', 'archaeology', 'historic'],
      description: 'Pre-empts Section 106 risk discovery during NEPA.',
      authoredSchema: [
        {
          key: 'shpoStatus',
          label: 'SHPO consultation status',
          type: 'enum',
          enumValues: ['not started', 'in progress', 'concurrence received', 'adverse effect', 'no historic properties'],
          required: false,
        },
        { key: 'consultationDate', label: 'Consultation date', type: 'date', required: false },
        { key: 'historicProperties', label: 'Historic properties on / near site', type: 'list', required: false, placeholder: 'Comma-separated, e.g. cemetery, mound, structure' },
        {
          key: 'tribalConsultation',
          label: 'Tribal consultation initiated?',
          type: 'boolean',
          required: false,
        },
        NOTES_FIELD,
      ],
    },
    ARCHAEOLOGICAL_SURVEY_ITEM,
  ],
  'power-availability': [
    {
      id: 'utility-letter',
      label: 'Utility service letter / capacity study',
      severity: 'critical',
      sedSectionsHelpful: ['grid', 'electricity'],
      relevanceTagPattern: ['utility', 'interconnect', 'service letter', 'capacity'],
      description: 'Mandatory for credible power conclusions. HIFLD distance is a poor proxy for available capacity.',
      authoredSchema: [
        { key: 'utilityName', label: 'Utility name', type: 'string', required: true },
        { key: 'capacityOffered_MW', label: 'Capacity offered', type: 'number', unit: 'MW', required: false },
        // Parity with the site-diligence utility-letter variant (same
        // requirementId, same transform): the service class changes what the
        // stated MW is WORTH on a 24/7 load — firm vs energy-only.
        {
          key: 'interconnectionClass',
          label: 'Interconnection class',
          type: 'enum',
          enumValues: ['firm', 'non-firm', 'cluster', 'energy-only', 'network'],
          required: false,
        },
        { key: 'serviceDate', label: 'Service date', type: 'date', required: false },
        NOTES_FIELD,
      ],
    },
    {
      id: 'iso-queue',
      label: 'ISO interconnection queue position',
      severity: 'recommended',
      sedSectionsHelpful: ['grid'],
      relevanceTagPattern: ['queue', 'interconnection', 'iso', 'rto'],
      description: 'Drives time-to-power conclusions; without it, timeline is generic.',
      authoredSchema: [
        { key: 'iso', label: 'ISO / RTO', type: 'enum', enumValues: ['PJM', 'MISO', 'ERCOT', 'CAISO', 'NYISO', 'ISO-NE', 'SPP', 'other'], required: false },
        { key: 'queuePosition', label: 'Queue position', type: 'string', required: false },
        { key: 'requestedMW', label: 'Requested capacity', type: 'number', unit: 'MW', required: false },
        { key: 'expectedClusterStudy', label: 'Expected cluster study', type: 'date', required: false },
        NOTES_FIELD,
      ],
    },
    {
      id: 'tariff',
      label: 'Retail tariff or PPA term sheet',
      severity: 'recommended',
      sedSectionsHelpful: ['electricity'],
      relevanceTagPattern: ['tariff', 'rate', 'ppa'],
      description: 'Site-specific economics; EIA wholesale rates are state-average.',
      authoredSchema: [
        { key: 'retailRate_centsKWh', label: 'Retail rate', type: 'number', unit: '¢/kWh', required: false },
        { key: 'contractTerm_years', label: 'Contract term', type: 'number', unit: 'years', required: false },
        { key: 'priceEscalator_pct', label: 'Price escalator', type: 'number', unit: '%', required: false },
        NOTES_FIELD,
      ],
    },
    BTM_VENDOR_QUOTE_ITEM,
  ],
  'water-resource': [
    ...WATER_SCREEN_DOCUMENTS,
    {
      id: 'discharge-permit',
      label: 'NPDES discharge permit',
      severity: 'recommended',
      sedSectionsHelpful: ['environmental', 'waterStress'],
      relevanceTagPattern: ['npdes', 'discharge', 'permit'],
      description: 'Without it, wastewater pathway is theoretical.',
      authoredSchema: [
        { key: 'permitNumber', label: 'Permit number', type: 'string', required: false },
        { key: 'permittedDischarge_MGD', label: 'Permitted discharge', type: 'number', unit: 'MGD', required: false },
        { key: 'receivingWater', label: 'Receiving water body', type: 'string', required: false },
        NOTES_FIELD,
      ],
    },
  ],
  'test-fit': [
    {
      id: 'survey',
      label: 'Topographic / boundary survey',
      severity: 'critical',
      sedSectionsHelpful: ['survey', 'topography', 'siteProfile', 'parcel'],
      relevanceTagPattern: ['survey', 'topo', 'boundary'],
      description: 'Required for any credible test-fit. Without it, slope and buildable area come from raster only.',
      authoredSchema: [
        { key: 'surveyor', label: 'Surveyor / firm', type: 'string', required: false, placeholder: 'Licensed surveyor name' },
        { key: 'surveyDate', label: 'Survey date', type: 'date', required: false },
        { key: 'surveyedAcreage', label: 'Surveyed acreage', type: 'number', unit: 'acres', required: false },
        { key: 'avgSlope_pct', label: 'Average slope', type: 'number', unit: '%', required: false },
        {
          key: 'verticalDatum',
          label: 'Vertical datum',
          type: 'enum',
          enumValues: ['NAVD88', 'NGVD29'],
          required: false,
        },
        NOTES_FIELD,
      ],
    },
    ALTA_SURVEY_ITEM,
    MINERAL_TITLE_ITEM,
    {
      id: 'utility-locate',
      label: 'Utility locate / encumbrance survey',
      severity: 'recommended',
      sedSectionsHelpful: ['parcel', 'siteProfile'],
      relevanceTagPattern: ['utility locate', 'encumbrance', 'easement'],
      description: 'Drives setback and footprint conclusions.',
      authoredSchema: [
        { key: 'locator', label: 'Locator / 811 ticket number', type: 'string', required: false, placeholder: 'e.g. ticket #2026-04-12345' },
        { key: 'locateDate', label: 'Locate date', type: 'date', required: false },
        { key: 'utilitiesLocated', label: 'Utilities located', type: 'list', required: false, placeholder: 'Comma-separated, e.g. gas, water, electric, fiber, sewer' },
        { key: 'encumbrances', label: 'Encumbrances / easements', type: 'list', required: false, placeholder: 'Comma-separated' },
        { key: 'setbackImpact_ft', label: 'Setback impact', type: 'number', unit: 'ft', required: false },
        NOTES_FIELD,
      ],
    },
  ],
  interconnection: [
    FIBER_SURVEY_ITEM,
    {
      id: 'latency-test',
      label: 'Latency test results',
      severity: 'recommended',
      sedSectionsHelpful: ['connectivity'],
      relevanceTagPattern: ['latency', 'rtt', 'ping', 'icmp'],
      description: 'Validates the modeled latency assumptions.',
      authoredSchema: [
        { key: 'targetEndpoint', label: 'Target endpoint', type: 'string', required: true, placeholder: 'e.g. ashburn-va, equinix-dc12' },
        { key: 'rtt_ms', label: 'Round-trip latency', type: 'number', unit: 'ms', required: false },
        { key: 'jitter_ms', label: 'Jitter', type: 'number', unit: 'ms', required: false },
        { key: 'packetLoss_pct', label: 'Packet loss', type: 'number', unit: '%', required: false },
        { key: 'testDate', label: 'Test date', type: 'date', required: false },
        {
          key: 'testTool',
          label: 'Test tool',
          type: 'enum',
          enumValues: ['ping', 'mtr', 'iperf3', 'speedtest', 'other'],
          required: false,
        },
        NOTES_FIELD,
      ],
    },
  ],
  'supply-chain-risk': [
    VENDOR_QUOTE_PACK_ITEM,
    BTM_VENDOR_QUOTE_ITEM,
  ],
  // PSR-1 (row 0.99290) — Public Sentiment Report. Scaffold checklist
  // — PSR-2 will widen this with community-engagement plans, prior
  // stakeholder maps, council-minutes archives, polling data, etc.
  'public-sentiment': [
    {
      id: 'community-feedback-summary',
      label: 'Community feedback summary or stakeholder log',
      severity: 'optional',
      sedSectionsHelpful: ['publicSentiment', 'siteResearch'],
      relevanceTagPattern: ['community', 'stakeholder', 'feedback', 'sentiment', 'engagement'],
      description: 'Prefilled from public records when SED public sentiment is available — upload stakeholder log to override.',
      // PSR-1 — authoredSchema lets ops capture stakeholder-log data
      // without uploading a full document. Fields stay narrow because
      // PSR-2 hardens the deeper Stakeholder Map section anyway.
      authoredSchema: [
        { key: 'overallStance', label: 'Overall community stance', type: 'enum', enumValues: ['supportive', 'mixed', 'neutral', 'oppositional'], required: false },
        { key: 'sentimentScore_0to100', label: 'Sentiment score (0-100)', type: 'number', required: false, placeholder: 'e.g. 62' },
        { key: 'keyThemes', label: 'Key themes (comma-separated)', type: 'string', required: false, placeholder: 'water use, jobs, traffic' },
        { key: 'stakeholderCount', label: 'Stakeholder count', type: 'number', required: false, placeholder: 'e.g. 24' },
        { key: 'lastEngagementDate', label: 'Last engagement date', type: 'date', required: false },
        { key: 'notes', label: 'Notes', type: 'string', required: false, placeholder: 'Optional narrative context' },
      ],
    },
  ],
  'custom-brief': [
    {
      id: 'diligence-source-pack',
      label: 'Diligence source pack or counterparty materials',
      severity: 'optional',
      sedSectionsHelpful: ['siteResearch', 'grid', 'regulatory'],
      relevanceTagPattern: ['diligence', 'brief', 'counterparty', 'loi', 'proposal', 'gas', 'btm'],
      description: 'Site-specific diligence materials referenced in the custom brief questions.',
      authoredSchema: [
        { key: 'notes', label: 'Notes', type: 'string', required: false, placeholder: 'LOIs, proposals, or internal memos referenced in the brief' },
      ],
    },
  ],
  'executive-summary': [
    {
      id: 'prior-report-pack',
      label: 'Prior report exports or IC memo (optional)',
      severity: 'optional',
      sedSectionsHelpful: ['siteResearch'],
      relevanceTagPattern: ['executive', 'synthesis', 'ic', 'memo', 'board'],
      description: 'Optional supporting materials beyond the selected in-app source reports.',
      authoredSchema: [
        { key: 'notes', label: 'Notes', type: 'string', required: false, placeholder: 'External memo or exported PDF referenced in synthesis focus' },
      ],
    },
    { ...VENDOR_QUOTE_PACK_ITEM, severity: 'optional' },
    { ...BTM_VENDOR_QUOTE_ITEM, severity: 'optional' },
  ],
  // FIN-1 (wf w7nuyquwd) — Financial Viability Report. The model is driven by
  // the site financial assumptions + pathway comparison (entered in-app), so
  // the checklist reuses the existing cross-listed `vendor-quote-pack` (already
  // schema-registered + alignment-mapped) to anchor the cost / financing
  // evidence — no net-new requirement id (keeps the coverage registries
  // exhaustive without new alignment rows).
  financial: [
    { ...VENDOR_QUOTE_PACK_ITEM, severity: 'optional' },
  ],
};

/** Returns the canonical checklist for a report type. */
export function getDocumentChecklistFor(typeId: ReportTypeId): ChecklistItem[] {
  const setup = getReportSetupChecklistFor(typeId);
  const docs = DOC_CHECKLISTS[typeId] ?? [];
  return [...setup, ...docs];
}

/** Aggregates checklists across multiple types, deduping by `id`. */
export function getDocumentChecklistAcross(typeIds: ReportTypeId[]): ChecklistItem[] {
  const seen = new Set<string>();
  const out: ChecklistItem[] = [];
  for (const id of typeIds) {
    for (const item of getDocumentChecklistFor(id)) {
      if (seen.has(item.id)) continue;
      seen.add(item.id);
      out.push(item);
    }
  }
  return out;
}

/* ── Matching uploaded docs ──────────────────────────────────────── */

export interface ChecklistMatchResult {
  /** Map slot id → docs that matched the slot's relevanceTagPattern. */
  matched: Map<string, SiteDocument[]>;
  /** Items with no matching upload. */
  missing: ChecklistItem[];
  /** Items that have at least one matching upload (or one of their replaceableBy alternates does). */
  satisfied: ChecklistItem[];
}

/**
 * Test whether a doc matches a checklist item's relevanceTagPattern.
 * Match is case-insensitive substring on the union of fileName, tags
 * (joined with spaces), and summary. Ping/ICMP use complete protocol/tool
 * cues so mapping/shipping filenames do not schedule a latency extraction.
 * A match on any one pattern fires.
 *
 * EDX-5 — exported so DocumentLibrary's SED-contribution filter pill
 * can re-use the same sync predicate the legacy matcher applies. The
 * async DM3 ranked matcher is the source of truth for the Coverage
 * Matrix; the pill filter uses the sync tag-pattern path to stay
 * snappy on per-keystroke filter changes (the wizard / preflight
 * surfaces already share this trade-off — see the
 * `matchUploadedDocsToChecklist` docstring).
 */
export function docMatchesPatterns(doc: SiteDocument, patterns: string[]): boolean {
  const source = `${doc.fileName ?? ''}\n${(doc.tags ?? []).join(' ')}\n${doc.summary ?? ''}`;
  const haystack = source.toLowerCase();
  // Latency cues name a protocol/tool, not the tail of mapping/shipping.
  // Preserve ordinary filename separators and CamelCase names; every other
  // established pattern retains its existing substring behavior.
  const cueText = source.replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2').toLowerCase();
  return patterns.some(p => {
    const pattern = p.toLowerCase();
    if (pattern === 'ping') return /(?:^|[^a-z0-9])ping(?:$|[^a-z0-9])/.test(cueText);
    if (pattern === 'icmp') return /(?:^|[^a-z0-9])icmp(?:$|[^a-z0-9])/.test(cueText);
    return haystack.includes(pattern);
  });
}

/**
 * Walk the checklist for a report type and bucket each item into
 * matched / satisfied / missing based on tag-pattern match against
 * `docs`. `replaceableBy` is followed: a slot is also satisfied when
 * any of its alternate slots have a match.
 *
 * NOTE: this is the legacy tag-pattern matcher. It has no relevance
 * scoring — a Purchase Option Agreement with passing mentions of
 * "land", "easement", "utility" satisfies every pattern, which is
 * the Redhead Feasibility regression that DM3 (`matchUploadedDocsToChecklistRanked`)
 * fixes. UI surfaces that need a sync answer (the wizard's
 * ReportPreferencesStep, the evidence-readiness score, the
 * auto-rerun suggester) still call this because converting them to
 * async would balloon the rerender surface; the ranked variant is
 * used at SED rebuild time + by the Site Evidence tab once a
 * cached row exists (DM4). Both helpers share the same
 * `ChecklistMatchResult` shape so callers can swap incrementally.
 */
export function matchUploadedDocsToChecklist(
  docs: SiteDocument[],
  typeId: ReportTypeId,
): ChecklistMatchResult {
  const checklist = getDocumentChecklistFor(typeId);
  // Soft-deleted docs must never satisfy a checklist slot: the deletedAt
  // contract keeps the record in `site.documents` for restore/audit, and
  // counting it as live evidence kept the auto-rerun banner citing a doc
  // the user had just deleted (Redhead, 2026-07-28).
  const userDocs = docs.filter(d => !isLibraryIndexDoc(d) && !d.deletedAt);
  const matched = new Map<string, SiteDocument[]>();

  // First pass: direct matches per item id.
  for (const item of checklist) {
    const hits = userDocs.filter(d => docMatchesPatterns(d, item.relevanceTagPattern));
    if (hits.length > 0) matched.set(item.id, hits);
  }

  // Second pass: collapse satisfied via replaceableBy.
  const satisfied: ChecklistItem[] = [];
  const missing: ChecklistItem[] = [];
  for (const item of checklist) {
    const direct = matched.has(item.id);
    const viaAlt = (item.replaceableBy ?? []).some(altId => matched.has(altId));
    if (direct || viaAlt) satisfied.push(item);
    else missing.push(item);
  }
  return { matched, missing, satisfied };
}

/**
 * Document Matching Quality DM3 — `matchUploadedDocsToChecklistLegacy`
 * is a deterministic stand-in for the legacy tag-pattern matcher that
 * the DM8 regression suite uses to demonstrate the Redhead bug
 * (one doc Primary for many items) BEFORE the DM3 fix. Identical
 * implementation to `matchUploadedDocsToChecklist` — re-exported under
 * an explicit "legacy" name so a future deletion of the unranked path
 * doesn't bury the regression-fixture call site.
 */
export const matchUploadedDocsToChecklistLegacy = matchUploadedDocsToChecklist;

/* ── DM3: ranked-match with primary-exclusivity + thresholds ─────── */

/**
 * Per-link evidence carried by the ranked matcher. Stored in
 * `RankedChecklistMatch.matches[itemId][docId]` so renderers can show
 * "Primary · Borehole_2026.pdf · 'Contains 12 borehole logs at 30m
 * depth'" without re-running the scorer.
 */
export interface RankedDocMatch {
  docId: string;
  role: 'primary' | 'supporting';
  /** 0..1 from `scoreDocAgainstChecklistItem`. */
  score: number;
  /** ≤200 chars from `scoreDocAgainstChecklistItem`. */
  rationale: string;
  /** ≤3 short quotes from `scoreDocAgainstChecklistItem`. */
  evidenceQuotes: readonly string[];
  /** Cache state of the underlying score row (for telemetry). */
  cached?: boolean;
}

export interface RankedChecklistMatch {
  /** Map slot id → ranked entries (Primary first, then Supporting). */
  matched: Map<string, RankedDocMatch[]>;
  /** Items with no qualifying upload. */
  missing: ChecklistItem[];
  /** Items with at least one Primary or one Supporting match. */
  satisfied: ChecklistItem[];
  /** Per-doc Primary count — used by DM7's over-tagging leaderboard. */
  primaryClaimCount: Map<string, number>;
  /**
   * (doc, item) pairs whose DM2 score was a transient failure — a timeout or
   * an API error (`RelevanceScore.transientFailure`) — and so count as no
   * match above. The matcher always sets it; absent (a result stored by an
   * older build) reads as 0. A result with failures is not final: the same
   * inputs, scored again, may add links. `siteCoverageMatchCache` re-runs it.
   */
  transientFailures?: number;
}

export interface MatchRankedOptions {
  siteId: string;
  /** Default 0.70. Per-tenant override comes through here (DM7). */
  primaryThreshold?: number;
  /** Default 0.40. Per-tenant override comes through here (DM7). */
  supportingThreshold?: number;
  /** Optional — drops any `(docId, itemId|sectionKey)` pair the user
   *  has disconnected (DM5). Identity matches `site.userDisconnectedLinks`
   *  (canonical field path on `SiteEntity` — `src/types/site.ts:157-164`)
   *  via `${docId}::${targetId}`. Callers normally pass
   *  `site.userDisconnectedLinks` directly; the `disconnectedAt` /
   *  `disconnectedBy` / `rationaleAtDisconnect` fields are ignored by
   *  the matcher but preserved on the site doc. */
  disconnectedLinks?: ReadonlyArray<{ docId: string; targetId: string }>;
  /**
   * Cancels the run: pairs still waiting for a DM2 scoring slot are dropped
   * and active SDK calls/retries are aborted. The matcher rejects with the
   * signal's reason; an abandoned model result is not cached or published.
   */
  signal?: AbortSignal;
}

/**
 * Ranked content-aware matcher. For every (doc, item) pair, calls the
 * DM2 scorer (Firestore-cached), then assigns roles:
 *
 *   - Score >= primaryThreshold (0.70 default) → Primary candidate.
 *   - Score >= supportingThreshold (0.40 default) → Supporting.
 *   - Below supporting threshold → no link at all.
 *
 * Two principled rules baked in:
 *
 *   1. A doc can be Primary for AT MOST ONE checklist item. Greedy
 *      assignment by score-desc, items sorted by their best-available
 *      top score so strong matches claim first (prevents a weak-primary
 *      item from stealing a doc that's a perfect primary for another).
 *   2. Minimum thresholds. Below 0.40 no link is recorded.
 *
 * This is the Redhead Feasibility fix — the Purchase Option Agreement
 * can now be Primary for AT MOST one item (likely Title / Parcel) and
 * is dropped from Geotech / Utility / Zoning / Flood entirely instead
 * of being listed everywhere with no rationale.
 */
export async function matchUploadedDocsToChecklistRanked(
  docs: SiteDocument[],
  typeId: ReportTypeId,
  opts: MatchRankedOptions,
): Promise<RankedChecklistMatch> {
  // Lazy import — keeps the DM2 surface (Firestore + Gemini) out of
  // the call-tree for callers that only need the legacy sync matcher.
  const { scoreDocAgainstChecklistItem, PRIMARY_SCORE_THRESHOLD, SUPPORTING_SCORE_THRESHOLD } =
    await import('../services/docMatching/relevanceScoring');

  const primaryT = opts.primaryThreshold ?? PRIMARY_SCORE_THRESHOLD;
  const supportingT = opts.supportingThreshold ?? SUPPORTING_SCORE_THRESHOLD;
  const checklist = getDocumentChecklistFor(typeId);
  // Soft-deleted docs must never satisfy a checklist slot: the deletedAt
  // contract keeps the record in `site.documents` for restore/audit, and
  // counting it as live evidence kept the auto-rerun banner citing a doc
  // the user had just deleted (Redhead, 2026-07-28).
  const userDocs = docs.filter(d => !isLibraryIndexDoc(d) && !d.deletedAt);

  // Build the disconnect filter as a Set for O(1) lookup. Identity is
  // `${docId}::${itemId}` — mirrors DM5's userDisconnectedLinks key.
  const disconnects = new Set(
    (opts.disconnectedLinks ?? []).map(d => `${d.docId}::${d.targetId}`),
  );
  const isDisconnected = (docId: string, itemId: string): boolean =>
    disconnects.has(`${docId}::${itemId}`);

  // Score every (doc, item) pair in parallel per-item. The DM2 cache
  // makes this cheap on rebuild; the first build pays for the Gemini
  // calls (capped by withTimeout per call). In the browser the fresh
  // calls take slots from DM2's throttle (`scoringThrottle`: 4 in flight,
  // 60 a minute, none inside the proxy's Retry-After), so this fan-out
  // cannot spend the proxy's per-IP bucket; cache reads stay parallel.
  const scoredByItem = new Map<string, Array<{ doc: SiteDocument; row: RankedDocMatch }>>();
  const scoreOpts = { siteId: opts.siteId, ...(opts.signal ? { signal: opts.signal } : {}) };
  // A timeout / API-error 0 drops out below like any sub-threshold score;
  // the count tells a caller that caches this result that it is not final.
  let transientFailures = 0;
  for (const item of checklist) {
    throwIfAborted(opts.signal);
    const rows = await Promise.all(
      userDocs.map(async d => {
        if (isDisconnected(d.id, item.id)) return null;
        const score = await scoreDocAgainstChecklistItem(d, item, scoreOpts);
        if (score.transientFailure) transientFailures += 1;
        if (score.score < supportingT) return null;
        const row: RankedDocMatch = {
          docId: d.id,
          // Role is set below after primary-exclusivity assignment;
          // start as 'supporting' so a doc that misses primary still
          // surfaces with its real score + rationale.
          role: 'supporting',
          score: score.score,
          rationale: score.rationale,
          evidenceQuotes: score.evidenceQuotes,
          cached: score.cached,
        };
        return { doc: d, row };
      }),
    );
    const ranked = rows
      .filter((r): r is { doc: SiteDocument; row: RankedDocMatch } => r !== null)
      .sort((a, b) => b.row.score - a.row.score);
    scoredByItem.set(item.id, ranked);
  }

  // Primary assignment — greedy, items-by-best-top-score-first so
  // strong matches claim before weak ones. A doc can be Primary for at
  // most one item (the Redhead-bug guard).
  const primaryClaimed = new Set<string>();
  const primaryClaimCount = new Map<string, number>();
  const itemsByBestScore = [...checklist].sort((a, b) =>
    (scoredByItem.get(b.id)?.[0]?.row.score ?? 0) -
    (scoredByItem.get(a.id)?.[0]?.row.score ?? 0),
  );
  const primaryByItem = new Map<string, RankedDocMatch | undefined>();
  for (const item of itemsByBestScore) {
    const ranked = scoredByItem.get(item.id) ?? [];
    // P3 — the one-primary-per-document rule guarded against a broad document
    // (a purchase agreement) claiming every slot. A document whose CONTENT
    // index names this requirement is a genuine primary for it even when it
    // is already primary elsewhere (a utility questionnaire is water AND
    // wastewater; an ecodev pack is flood AND wetlands AND pipelines).
    const winner = ranked.find(r => r.row.score >= primaryT
      && (!primaryClaimed.has(r.doc.id) || documentContentNamesRequirement(r.doc, item.id)));
    if (winner) {
      primaryClaimed.add(winner.doc.id);
      primaryClaimCount.set(winner.doc.id, (primaryClaimCount.get(winner.doc.id) ?? 0) + 1);
      winner.row.role = 'primary';
      primaryByItem.set(item.id, winner.row);
    } else {
      primaryByItem.set(item.id, undefined);
    }
  }

  // Final matched map — Primary first (when assigned), then Supporting
  // sorted by score desc.
  const matched = new Map<string, RankedDocMatch[]>();
  for (const item of checklist) {
    const ranked = scoredByItem.get(item.id) ?? [];
    const primary = primaryByItem.get(item.id);
    const supporting = ranked
      .filter(r => r.row.docId !== primary?.docId)
      .map(r => r.row);
    matched.set(item.id, primary ? [primary, ...supporting] : supporting);
  }

  // Satisfied/missing — an item is satisfied when it has a Primary, or
  // any of its replaceableBy alternates has a Primary. Supporting
  // alone does NOT satisfy a checklist item — that was a legacy
  // behaviour that contributed to the false-confidence problem the
  // pack fixes.
  const hasPrimary = (itemId: string): boolean =>
    !!matched.get(itemId)?.some(r => r.role === 'primary');
  const satisfied: ChecklistItem[] = [];
  const missing: ChecklistItem[] = [];
  for (const item of checklist) {
    const direct = hasPrimary(item.id);
    const viaAlt = (item.replaceableBy ?? []).some(alt => hasPrimary(alt));
    if (direct || viaAlt) satisfied.push(item);
    else missing.push(item);
  }

  throwIfAborted(opts.signal);
  return { matched, missing, satisfied, primaryClaimCount, transientFailures };
}

/* ── Confidence floor ────────────────────────────────────────────── */

/**
 * When any `severity: 'critical'` checklist item is missing, the
 * report's overall confidence is capped at 0.75 (Documents_On_Site
 * Prompt 9). The cap is configurable per-tenant via the AdminPanel —
 * `cap` is exposed here so callers can apply the tenant override
 * without re-deriving the rule.
 */
export const DEFAULT_MISSING_CRITICAL_CAP = 0.75;

export interface ConfidenceFloorResult {
  /** Number of `critical` items missing — drives banner copy. */
  missingCritical: number;
  /** Cap to apply (0..1). When missingCritical === 0, this is 1.0. */
  cap: number;
  /** The raw missing-critical items so the banner can list them. */
  missingCriticalItems: ChecklistItem[];
}

export function computeConfidenceFloor(
  match: ChecklistMatchResult,
  cap: number = DEFAULT_MISSING_CRITICAL_CAP,
): ConfidenceFloorResult {
  const missingCriticalItems = match.missing.filter(i => i.severity === 'critical');
  return {
    missingCritical: missingCriticalItems.length,
    cap: missingCriticalItems.length > 0 ? cap : 1.0,
    missingCriticalItems,
  };
}

/* ── Requirement lookup (hoisted from applyRequirementDocAutoFill) ──── */

let checklistItemIndex: Map<string, ChecklistItem> | null = null;

/**
 * The checklist item for a requirement id, searched across EVERY report
 * type (first declaration wins). One shared index — the extraction
 * pipeline, stamp filter and review panel must all resolve the same item
 * or their authoredSchema-keyed behaviour diverges.
 */
export function checklistItemById(requirementId: string): ChecklistItem | undefined {
  if (!checklistItemIndex) {
    checklistItemIndex = new Map();
    for (const items of Object.values(DOC_CHECKLISTS)) {
      for (const item of items) {
        if (!checklistItemIndex.has(item.id)) checklistItemIndex.set(item.id, item);
      }
    }
  }
  return checklistItemIndex.get(requirementId);
}

/** Every requirement id across every report type's checklist (deduped). */
export function checklistRequirementIds(): string[] {
  const ids = new Set<string>();
  for (const items of Object.values(DOC_CHECKLISTS)) {
    for (const item of items) ids.add(item.id);
  }
  return [...ids];
}
